import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { tablesAPI } from '../utils/api';
import RecordModal from './RecordModal';

function TableView() {
  const { tableName } = useParams();
  const [data, setData] = useState([]);
  const [schema, setSchema] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [modalState, setModalState] = useState({ isOpen: false, mode: 'create', record: null });

  useEffect(() => {
    fetchSchema();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, page, search, sortBy, sortOrder]);

  const fetchSchema = async () => {
    try {
      const response = await tablesAPI.getSchema(tableName);
      setSchema(response.data);
    } catch (err) {
      setError('Failed to fetch table schema');
      console.error('Schema error:', err);
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await tablesAPI.getData(tableName, { page, search, limit: 20, sortBy, sortOrder });
      setData(response.data.data);
      setTotalPages(response.data.totalPages);
      setError('');
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Data error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (columnName) => {
    if (sortBy === columnName) {
      // Toggle direction if same column
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // New column, default to ASC
      setSortBy(columnName);
      setSortOrder('ASC');
    }
    setPage(1); // Reset to first page on sort change
  };

  const handleCreate = () => {
    setModalState({ isOpen: true, mode: 'create', record: null });
  };

  const handleEdit = (record) => {
    setModalState({ isOpen: true, mode: 'edit', record });
  };

  const handleDelete = async (record) => {
    const pkColumn = schema.find(col => col.isPrimaryKey);
    if (!pkColumn) {
      alert('Cannot delete: table has no primary key');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      await tablesAPI.deleteRecord(tableName, record[pkColumn.name]);
      fetchData();
    } catch (err) {
      alert('Failed to delete record: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleModalClose = () => {
    setModalState({ isOpen: false, mode: 'create', record: null });
  };

  const handleModalSave = async (formData) => {
    try {
      if (modalState.mode === 'create') {
        await tablesAPI.createRecord(tableName, formData);
      } else {
        const pkColumn = schema.find(col => col.isPrimaryKey);
        await tablesAPI.updateRecord(tableName, modalState.record[pkColumn.name], formData);
      }
      handleModalClose();
      fetchData();
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Failed to save record');
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const getPrimaryKeyValue = (record) => {
    const pkColumn = schema.find(col => col.isPrimaryKey);
    return pkColumn ? record[pkColumn.name] : null;
  };

  const displayColumns = schema.filter(col => !col.isIdentity || col.isPrimaryKey);

  return (
    <div>
      {/* Actions Bar - Sticky */}
      <div className="sticky top-0 z-10 bg-gray-100 py-4 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 mb-6 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearchChange}
            className="input w-full sm:w-64"
          />
          <button
            onClick={handleCreate}
            className="btn btn-primary whitespace-nowrap"
          >
            + Add New Record
          </button>
          {/* Sort Indicator */}
          {sortBy && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm">
              <span>Sorted by: <strong>{sortBy}</strong> ({sortOrder === 'ASC' ? 'A-Z' : 'Z-A'})</span>
              <button
                onClick={() => { setSortBy(''); setSortOrder('ASC'); }}
                className="text-primary-600 hover:text-primary-800"
                title="Clear sort"
              >
                ✕
              </button>
            </div>
          )}
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-gray-700 whitespace-nowrap">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Data Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="text-lg text-gray-600">Loading data...</div>
        </div>
      ) : data.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600">No records found</p>
          <button
            onClick={handleCreate}
            className="btn btn-primary mt-4"
          >
            Create First Record
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {displayColumns.map((col) => (
                      <th
                        key={col.name}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort(col.name)}
                        title={`Sort by ${col.name}`}
                      >
                        <div className="flex items-center gap-1">
                          <span>{col.name}</span>
                          {col.isPrimaryKey && (
                            <span className="text-primary-600" title="Primary Key">🔑</span>
                          )}
                          {sortBy === col.name ? (
                            <span className="text-primary-600 ml-1">
                              {sortOrder === 'ASC' ? '▲' : '▼'}
                            </span>
                          ) : (
                            <span className="text-gray-300 ml-1">⇅</span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((record, index) => (
                    <tr key={getPrimaryKeyValue(record) || index} className="hover:bg-gray-50">
                      {displayColumns.map((col) => (
                        <td key={col.name} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatValue(record[col.name])}
                        </td>
                      ))}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(record)}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(record)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Record Modal */}
      {modalState.isOpen && (
        <RecordModal
          mode={modalState.mode}
          record={modalState.record}
          schema={schema}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

function formatValue(value) {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">null</span>;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (value instanceof Date) {
    return value.toLocaleString();
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

export default TableView;
